/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CapacityDetailModal.tsx
 *     Feature         :   117-operational-dashboards / 138-dashboard-data-enrichment
 *     Description     :   Modal showing detailed capacity metrics with inline edit support
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { formatEastern } from '@/utils/dateFormatter';
import { Activity, Server, Zap, Cpu, Pencil, Check, RotateCcw } from 'lucide-react';
import type { Capacity, HealthStatus } from '../../types/operations';
import { nullDisplay } from '../../utils/nullDisplay';
import { useAuth } from '../../hooks/useAuth';
import { useUpdateCapacity, type CapacityUpdatePayload } from '../../services/operations/capacitiesService';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';
import { DetailModal } from './DetailModal';

interface CapacityDetailModalProps {
  capacity: (Capacity & { workspaces?: unknown[]; snapshots?: unknown[] }) | null;
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const HEALTH_BADGE: Record<HealthStatus, string> = {
  Healthy: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  Warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  Critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  // Nothing measured this capacity's CU (Spec 286 T160 item 5, migration 399).
  Unknown: 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]',
};

const CRITICALITY_OPTIONS = ['ClientFacing', 'Shared', 'Foundation'];
const STATUS_OPTIONS = ['Active', 'Suspended', 'Paused', 'Deprovisioned'];

export const CapacityDetailModal: React.FC<CapacityDetailModalProps> = ({
  capacity,
  isLoading = false,
  isOpen,
  onClose,
}) => {
  const { isAdmin } = useAuth();
  const updateMutation = useUpdateCapacity();
  const [isEditing, setIsEditing] = useState(false);
  const [editFields, setEditFields] = useState<CapacityUpdatePayload>({});

  // Reset edit state when capacity changes or modal closes
  useEffect(() => {
    if (capacity) {
      setEditFields({
        criticality: capacity.criticality || '',
        status: capacity.current_status || '',
        billing_tag: (capacity as any).billing_tag || '',
        tenant_key_id: (capacity as any).tenant_key_id || '',
      });
    }
    setIsEditing(false);
  }, [capacity?.capacity_id, isOpen]);

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(
    (capacity?.workspaces as any[]) ?? []
  );

  const handleSave = async () => {
    if (!capacity) return;
    try {
      await updateMutation.mutateAsync({
        id: capacity.capacity_id,
        payload: editFields,
      });
      setIsEditing(false);
    } catch {
      // Error is handled by mutation state
    }
  };

  const handleReactivate = async () => {
    if (!capacity) return;
    try {
      await updateMutation.mutateAsync({
        id: capacity.capacity_id,
        payload: { status: 'Active' },
      });
      setIsEditing(false);
    } catch {
      // Error is handled by mutation state
    }
  };

  if (!isOpen) return null;

  const isInactive = capacity?.capacity_is_active === false;

  return (
    <DetailModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Capacity' : 'Capacity Details'}
    >
      {/* Body-level Edit action (DetailModal owns the header ✕) */}
      {capacity && !isEditing && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-[var(--color-accent)] hover:bg-blue-50 dark:hover:bg-blue-900/20"
            title="Edit capacity"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        </div>
      )}

      {/* Content */}
      <div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            </div>
          ) : capacity ? (
            <div className="space-y-6">
              {/* Inactive Banner */}
              {isInactive && (
                <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 dark:border-orange-800 dark:bg-orange-900/20">
                  <div className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                      This capacity is inactive
                    </span>
                  </div>
                  <button
                    onClick={handleReactivate}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                  >
                    <RotateCcw className={clsx('h-3 w-3', updateMutation.isPending && 'animate-spin')} />
                    Reactivate
                  </button>
                </div>
              )}

              {/* Mutation Error */}
              {updateMutation.isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  Failed to update capacity. Please try again.
                </div>
              )}

              {/* Capacity Info */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-[var(--color-text-primary)]">
                    {capacity.capacity_name}
                  </h3>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    {capacity.capacity_type} · {capacity.region || 'Unknown region'}
                  </p>
                </div>
                <span
                  className={clsx(
                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
                    HEALTH_BADGE[capacity.health_status as HealthStatus] || 'bg-gray-100 text-gray-800'
                  )}
                >
                  {capacity.health_status}
                </span>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <MetricCard
                  icon={<Activity className="h-4 w-4 text-blue-500" />}
                  label="Utilization"
                  value={capacity.avg_utilization_percent === null ? 'no data' : `${capacity.avg_utilization_percent.toFixed(1)}%`}
                />
                <MetricCard
                  icon={<Cpu className="h-4 w-4 text-indigo-500" />}
                  label="SKU / CU"
                  value={`${nullDisplay(capacity.sku)} / ${nullDisplay(capacity.cu_count)}`}
                />
                <MetricCard
                  icon={<Zap className="h-4 w-4 text-red-500" />}
                  label="Throttling (min)"
                  value={nullDisplay(capacity.throttling_minutes)}
                />
                <MetricCard
                  icon={<Server className="h-4 w-4 text-violet-500" />}
                  label="Workspaces"
                  value={String(capacity.workspace_count)}
                />
              </div>

              {/* Edit Form */}
              {isEditing ? (
                <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-800 dark:bg-blue-900/10">
                  <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                    Edit Fields
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
                        Criticality
                      </label>
                      <select
                        value={editFields.criticality || ''}
                        onChange={(e) => setEditFields((prev) => ({ ...prev, criticality: e.target.value }))}
                        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
                      >
                        <option value="">— Select —</option>
                        {CRITICALITY_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
                        Status
                      </label>
                      <select
                        value={editFields.status || ''}
                        onChange={(e) => setEditFields((prev) => ({ ...prev, status: e.target.value }))}
                        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
                        Billing Tag
                      </label>
                      <input
                        type="text"
                        value={editFields.billing_tag || ''}
                        onChange={(e) => setEditFields((prev) => ({ ...prev, billing_tag: e.target.value }))}
                        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
                        placeholder="e.g. PROJ-001"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
                        Tenant Key ID
                      </label>
                      <input
                        type="text"
                        value={editFields.tenant_key_id || ''}
                        onChange={(e) => setEditFields((prev) => ({ ...prev, tenant_key_id: e.target.value }))}
                        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
                        placeholder="e.g. tenant-key-123"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updateMutation.isPending}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Check className={clsx('h-3 w-3', updateMutation.isPending && 'animate-spin')} />
                      {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Details (Read-only) */
                <div className="rounded-lg border border-[var(--color-border)]">
                  <table className="w-full text-sm">
                    <tbody>
                      <DetailRow label="Capacity ID" value={capacity.capacity_id} />
                      <DetailRow label="Type" value={capacity.capacity_type} />
                      <DetailRow label="Criticality" value={capacity.criticality || 'N/A'} />
                      <DetailRow label="Region" value={capacity.region || 'N/A'} />
                      <DetailRow label="Client Account" value={capacity.client_account_id || 'N/A'} />
                      <DetailRow label="Current Status" value={capacity.current_status || 'N/A'} />
                      <DetailRow label="Provisioning State" value={nullDisplay(capacity.provisioning_state)} />
                      <DetailRow label="Overage (min)" value={nullDisplay(capacity.overage_minutes)} />
                      <DetailRow label="Operations Count" value={nullDisplay(capacity.operations_count)} />
                      {isAdmin() && capacity.admin_emails && (
                        <DetailRow label="Admin Contacts" value={capacity.admin_emails} />
                      )}
                      <DetailRow
                        label="Last Snapshot"
                        value={
                          capacity.last_snapshot_date
                            ? formatEastern(capacity.last_snapshot_date)
                            : 'N/A'
                        }
                      />
                    </tbody>
                  </table>
                </div>
              )}

              {/* Workspaces */}
              {!isEditing && capacity.workspaces && (capacity.workspaces as any[]).length > 0 && (
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
                    Workspaces ({(capacity.workspaces as any[]).length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-[var(--color-border)]">
                    <DualScrollTable>
                    <table className="w-full text-xs">
                      <thead className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium text-[var(--color-text-secondary)]">
                            Name
                          </th>
                          <th className="px-2 py-1.5 text-left font-medium text-[var(--color-text-secondary)]">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-border)]">
                        {pageItems.map((ws: any) => (
                          <tr key={ws.workspace_id}>
                            <td className="px-3 py-1.5 text-[var(--color-text-primary)]">
                              {ws.workspace_name}
                            </td>
                            <td className="px-3 py-1.5">
                              <span
                                className={clsx(
                                  'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium',
                                  ws.is_active
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                )}
                              >
                                {ws.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </DualScrollTable>
                  </div>
                  <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-[var(--color-text-secondary)]">
                No capacity data available
              </p>
            </div>
          )}
      </div>
    </DetailModal>
  );
};

// Sub-components
const MetricCard: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-xs text-[var(--color-text-secondary)]">{label}</span>
    </div>
    <p className="mt-1 text-lg font-bold text-[var(--color-text-primary)]">{value}</p>
  </div>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <tr className="border-b border-[var(--color-border)] last:border-b-0">
    <td className="px-4 py-2 font-medium text-[var(--color-text-secondary)]">{label}</td>
    <td className="px-4 py-2 text-[var(--color-text-primary)]">{value}</td>
  </tr>
);

export default CapacityDetailModal;
